import React, { useMemo } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { Appearance, loadStripe, StripeElementsOptions } from '@stripe/stripe-js';
import { FiCurrency } from '../types';
import { useLocale } from '../contexts/LocaleContext';
import { getStripeLocale } from './stripeLocale';
import { getStripePublishableKey, getStripeAccountId } from './stripeConfig';
import useTenancy from '../hooks/useTenancy';

const APPEARANCE: Appearance = {
  theme: 'flat',
  variables: {
    fontFamily:
      "'MessinaSans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
    fontSizeBase: '16px',
    fontWeightNormal: '400',
    fontWeightMedium: '500',
    fontWeightBold: '600',
    borderRadius: '8px',
    colorPrimary: '#000000',
    colorText: '#000000',
    colorTextSecondary: '#737373',
    colorDanger: '#ff004f',
    spacingGridRow: '12px',
  },
  rules: {
    '.Label': {
      fontWeight: '500',
      fontSize: '14px',
      marginBottom: '6px',
    },
    '.Input::placeholder': {
      color: 'transparent',
    },
  },
};

type StripeElementsProviderProps = {
  children: React.ReactNode;
  currency?: FiCurrency;
  /**
   * Payment method types stripe must not offer, regardless of the account's payment method
   * configuration. Used to keep klarna out of flows that can't complete a klarna order.
   *
   * Stripe types this as `string[]` but validates it at runtime, throwing an IntegrationError on
   * anything outside its list. Wallets are not on that list: Link is suppressed with the
   * PaymentElement's `wallets: { link: 'never' }`, not here.
   */
  excludedPaymentMethodTypes?: string[];
} & ({ mode: 'payment'; amountInCents: number } | { mode: 'setup' });

/**
 * Wraps children in stripe's Elements provider for use with PaymentElement, AddressElement,
 * and LinkAuthenticationElement. Supports both payment mode (immediate charge) and setup mode
 * (save payment method for future use).
 */
export default function StripeElementsProvider(props: StripeElementsProviderProps) {
  const { children, currency = FiCurrency.USD, mode, excludedPaymentMethodTypes } = props;
  const amount = mode === 'payment' ? props.amountInCents : undefined;
  const { language } = useLocale();
  const stripeLocale = getStripeLocale(language);
  const tenancy = useTenancy();

  /**
   * Memoized so the stripe instance is created once per component lifetime. Uses recurly's
   * publishable key + fi's stripe connect account id per recurly's third-party checkout docs.
   * Switches between prod and test Stripe keys based on tenancy (similar to FiRecurlyProvider).
   */
  const stripePromise = useMemo(() => {
    const key = getStripePublishableKey();
    const accountId = getStripeAccountId();
    if (!key || !accountId) {
      console.warn('Stripe publishable key or account ID is not set — stripe elements will not load');
      return null;
    }
    return loadStripe(key, { stripeAccount: accountId });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- tenancy changes which key getStripePublishableKey() returns
  }, [tenancy]);

  const options: StripeElementsOptions = useMemo(() => {
    const base = {
      currency: currency.toLowerCase(),
      locale: stripeLocale,
      loader: 'auto' as const,
      paymentMethodCreation: 'manual' as const,
      appearance: APPEARANCE,
      ...(excludedPaymentMethodTypes?.length ? { excludedPaymentMethodTypes } : {}),
    };

    if (mode === 'payment' && amount !== undefined) {
      // payment mode + amount + off_session is recurly's required config for its third-party
      // (stripe elements) checkout, including klarna bnpl: recurly rejects confirmation tokens
      // whose setup_future_usage isn't off_session (its server-side PaymentIntent hardcodes it).
      // klarna's pay now / pay later / installments choice happens on klarna's redirect page
      // (driven by recurly passing subscription details on the intent), not in the element, so a
      // per-method setup_future_usage override is neither needed nor accepted here.
      return {
        ...base,
        mode: 'payment' as const,
        amount,
        setupFutureUsage: 'off_session' as const,
      };
    }

    return {
      ...base,
      mode: 'setup' as const,
      setupFutureUsage: 'off_session' as const,
    };
  }, [mode, amount, currency, stripeLocale, excludedPaymentMethodTypes]);

  return (
    <Elements stripe={stripePromise} options={options}>
      {children}
    </Elements>
  );
}
